import {
  Box,
  Button,
  Grid,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { DashboardGetParams } from '@/lib/utils/dashboardQueryParams'
import { toYmd } from '@/lib/utils/dashboardQueryParams'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

export type DashboardFilterKind = 'day' | 'week' | 'month' | 'year' | 'custom'

const WEEK_OPTS = { weekStartsOn: 1 as const } // Monday

function mondayOfWeekContaining(date: Date): Date {
  return startOfWeek(startOfDay(date), WEEK_OPTS)
}

function sundayOfWeek(monday: Date): Date {
  return endOfWeek(monday, WEEK_OPTS)
}

function isCurrentWeekRange(monday: Date): boolean {
  const today = startOfDay(new Date())
  const currentWeekMonday = mondayOfWeekContaining(today)
  return monday.getTime() === currentWeekMonday.getTime()
}

function buildQuery(
  kind: DashboardFilterKind,
  day: Date,
  weekMonday: Date,
  month: { y: number; m: number },
  year: number,
  custom: { from: Date; to: Date }
): DashboardGetParams {
  switch (kind) {
    case 'day':
      return { date: toYmd(startOfDay(day)) }
    case 'week': {
      const mon = startOfDay(weekMonday)
      const sun = startOfDay(sundayOfWeek(mon))
      return { date_from: toYmd(mon), date_to: toYmd(isCurrentWeekRange(mon) ? startOfDay(new Date()) : sun) }
    }
    case 'month': {
      const first = startOfDay(new Date(month.y, month.m, 1))
      const last = startOfDay(endOfMonth(first))
      return { date_from: toYmd(first), date_to: toYmd(last) }
    }
    case 'year':
      return { year }
    case 'custom': {
      let a = startOfDay(custom.from)
      let b = startOfDay(custom.to)
      if (a.getTime() > b.getTime()) [a, b] = [b, a]
      return { date_from: toYmd(a), date_to: toYmd(b) }
    }
  }
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

interface DashboardDateFilterBarProps {
  onQueryChange: (params: DashboardGetParams, filterKind: DashboardFilterKind) => void
  defaultFilterKind?: DashboardFilterKind
}

export function DashboardDateFilterBar({
  onQueryChange,
  defaultFilterKind = 'day',
}: DashboardDateFilterBarProps) {
  const colors = useThemeColors()
  const today = useMemo(() => startOfDay(new Date()), [])
  const currentMonthStart = useMemo(() => startOfMonth(today), [today])
  const currentMonthEnd = useMemo(() => startOfDay(endOfMonth(today)), [today])

  const [filterKind, setFilterKind] = useState<DashboardFilterKind>(defaultFilterKind)

  const [dayCommitted, setDayCommitted] = useState(() => today)
  const [weekCommittedMonday, setWeekCommittedMonday] = useState(() => mondayOfWeekContaining(today))
  const [monthCommitted, setMonthCommitted] = useState(() => ({
    y: today.getFullYear(),
    m: today.getMonth(),
  }))
  const [yearCommitted, setYearCommitted] = useState(() => today.getFullYear())
  const [customCommitted, setCustomCommitted] = useState(() => ({ from: currentMonthStart, to: currentMonthEnd }))

  const emitQuery = useCallback(() => {
    onQueryChange(
      buildQuery(filterKind, dayCommitted, weekCommittedMonday, monthCommitted, yearCommitted, customCommitted),
      filterKind
    )
  }, [
    filterKind,
    dayCommitted,
    weekCommittedMonday,
    monthCommitted,
    yearCommitted,
    customCommitted,
    onQueryChange,
  ])

  useEffect(() => {
    emitQuery()
  }, [emitQuery])

  const [dayPopoverOpen, setDayPopoverOpen] = useState(false)
  const [weekPopoverOpen, setWeekPopoverOpen] = useState(false)
  const [monthPopoverOpen, setMonthPopoverOpen] = useState(false)
  const [yearPopoverOpen, setYearPopoverOpen] = useState(false)
  const [customFromOpen, setCustomFromOpen] = useState(false)
  const [customToOpen, setCustomToOpen] = useState(false)

  // Draft state (popover)
  const [draftDay, setDraftDay] = useState(() => today)
  const [draftDayViewMonth, setDraftDayViewMonth] = useState(() => startOfMonth(today))

  const [draftWeekMonday, setDraftWeekMonday] = useState(() => mondayOfWeekContaining(today))

  const [draftMonthY, setDraftMonthY] = useState(today.getFullYear())
  const [draftMonthIndex, setDraftMonthIndex] = useState(today.getMonth())

  const [draftYear, setDraftYear] = useState(today.getFullYear())

  const [draftCustomFrom, setDraftCustomFrom] = useState(() => currentMonthStart)
  const [draftCustomTo, setDraftCustomTo] = useState(() => currentMonthEnd)
  const [draftCustomFromView, setDraftCustomFromView] = useState(() => startOfMonth(today))
  const [draftCustomToView, setDraftCustomToView] = useState(() => startOfMonth(today))

  const syncDraftDay = () => {
    setDraftDay(dayCommitted)
    setDraftDayViewMonth(startOfMonth(dayCommitted))
  }

  const syncDraftWeek = () => {
    setDraftWeekMonday(weekCommittedMonday)
  }

  const syncDraftMonth = () => {
    setDraftMonthY(monthCommitted.y)
    setDraftMonthIndex(monthCommitted.m)
  }

  const syncDraftYear = () => {
    setDraftYear(yearCommitted)
  }

  const syncDraftCustomFrom = () => {
    setDraftCustomFrom(customCommitted.from)
    setDraftCustomFromView(startOfMonth(customCommitted.from))
  }

  const syncDraftCustomTo = () => {
    setDraftCustomTo(customCommitted.to)
    setDraftCustomToView(startOfMonth(customCommitted.to))
  }

  const dayButtonLabel = format(dayCommitted, 'MMM d, yyyy')

  const weekButtonLabel = useMemo(() => {
    const mon = startOfDay(weekCommittedMonday)
    const sun = startOfDay(sundayOfWeek(mon))
    return `${format(mon, 'MMM d')} - ${format(isCurrentWeekRange(mon) ? startOfDay(new Date()) : sun, 'MMM d, yyyy')}`
  }, [weekCommittedMonday])

  const monthButtonLabel = format(new Date(monthCommitted.y, monthCommitted.m, 1), 'MMMM yyyy')

  const yearButtonLabel = String(yearCommitted)

  const customFromLabel = format(customCommitted.from, 'MMM d, yyyy')
  const customToLabel = format(customCommitted.to, 'MMM d, yyyy')

  const handleFilterKindChange = (next: DashboardFilterKind) => {
    const prev = filterKind
    setFilterKind(next)

    if (next === 'week' && prev === 'day') {
      setWeekCommittedMonday(mondayOfWeekContaining(dayCommitted))
    }
    if (next === 'month' && (prev === 'day' || prev === 'week')) {
      const d = prev === 'day' ? dayCommitted : weekCommittedMonday
      setMonthCommitted({ y: d.getFullYear(), m: d.getMonth() })
    }
    if (next === 'year' && prev === 'month') {
      setYearCommitted(monthCommitted.y)
    }
    if (next === 'custom') {
      setCustomCommitted({ from: currentMonthStart, to: currentMonthEnd })
    }
  }

  const calendarGridDays = (viewMonth: Date) => {
    const ms = startOfMonth(viewMonth)
    const me = endOfMonth(viewMonth)
    const gridStart = startOfWeek(ms, WEEK_OPTS)
    const gridEnd = endOfWeek(me, WEEK_OPTS)
    return eachDayOfInterval({ start: gridStart, end: gridEnd })
  }

  const DayCalendar = ({
    viewMonth,
    onViewMonthChange,
    selectedDay,
    onSelectDay,
    disableDayClick,
    highlightWeekMonday,
    hideMonthNav,
  }: {
    viewMonth: Date
    onViewMonthChange: (d: Date) => void
    selectedDay: Date
    onSelectDay?: (d: Date) => void
    disableDayClick?: boolean
    highlightWeekMonday?: Date
    hideMonthNav?: boolean
  }) => {
    const days = calendarGridDays(viewMonth)
    const weekStart = highlightWeekMonday ? startOfDay(highlightWeekMonday) : null
    const weekEnd = weekStart ? startOfDay(sundayOfWeek(weekStart)) : null

    return (
      <Box>
        {hideMonthNav ? (
          <Text fontWeight="semibold" fontSize="sm" textAlign="center" mb={2}>
            {format(viewMonth, 'MMMM yyyy')}
          </Text>
        ) : (
          <HStack justify="space-between" mb={2}>
            <IconButton
              aria-label="Previous month"
              icon={<ChevronLeft size={18} />}
              size="sm"
              variant="ghost"
              onClick={() => onViewMonthChange(addMonths(viewMonth, -1))}
            />
            <Text fontWeight="semibold" fontSize="sm">
              {format(viewMonth, 'MMMM yyyy')}
            </Text>
            <IconButton
              aria-label="Next month"
              icon={<ChevronRight size={18} />}
              size="sm"
              variant="ghost"
              onClick={() => onViewMonthChange(addMonths(viewMonth, 1))}
            />
          </HStack>
        )}
        <Grid templateColumns="repeat(7, 1fr)" gap={1} fontSize="xs" color={colors.textSecondary} mb={1}>
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
            <Text key={d} textAlign="center" fontWeight="semibold">
              {d}
            </Text>
          ))}
        </Grid>
        <Grid templateColumns="repeat(7, 1fr)" gap={1}>
          {days.map((d) => {
            const inMonth = isSameMonth(d, viewMonth)
            const selected = isSameDay(d, selectedDay)
            const inWeek =
              weekStart &&
              weekEnd &&
              isWithinInterval(startOfDay(d), { start: weekStart, end: weekEnd })
            return (
              <Button
                key={d.toISOString()}
                size="xs"
                h="32px"
                variant="ghost"
                isDisabled={!inMonth || Boolean(disableDayClick)}
                pointerEvents={disableDayClick ? 'none' : undefined}
                bg={
                  selected
                    ? 'blue.500'
                    : inWeek
                      ? 'blue.300'
                      : inMonth
                        ? 'transparent'
                        : 'transparent'
                }
                color={selected ? 'white' : inMonth ? colors.heading : colors.textMuted}
                _hover={disableDayClick ? {} : { bg: selected ? 'blue.600' : 'gray.300' }}
                onClick={() => onSelectDay?.(startOfDay(d))}
              >
                {format(d, 'd')}
              </Button>
            )
          })}
        </Grid>
      </Box>
    )
  }

  const dateButtonProps = {
    size: 'sm' as const,
    variant: 'outline' as const,
    borderColor: colors.border,
    leftIcon: <Icon as={Calendar} boxSize={4} color={colors.textMuted} />,
    rightIcon: <Icon as={ChevronDown} boxSize={4} />,
  }

  return (
    <HStack align="flex-end" flexWrap="wrap" gap={6}>
      <VStack align="flex-start" spacing={1}>
        {/* <Text fontSize="xs" fontWeight="semibold" color={colors.textSecondary}>
          Filter by:
        </Text> */}
        <Select
          size="sm"
          w="140px"
          borderColor={colors.border}
          value={filterKind}
          onChange={(e) => handleFilterKindChange(e.target.value as DashboardFilterKind)}
        >
          <option value="day">Daily</option>
          <option value="week">Weekly</option>
          <option value="month">Monthly</option>
          <option value="year">Yearly</option>
          <option value="custom">Custom</option>
        </Select>
      </VStack>

      {filterKind === 'custom' ? (
        <HStack align="flex-end" spacing={4} flexWrap="wrap">
          <VStack align="flex-start" spacing={1}>
            {/* <Text fontSize="xs" fontWeight="semibold" color={colors.textSecondary}>
              From:
            </Text> */}
            <Popover
              isOpen={customFromOpen}
              onClose={() => setCustomFromOpen(false)}
              closeOnBlur={false}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <HStack spacing={0}>
                  <Box
                    px={3}
                    h="32px"
                    display="inline-flex"
                    alignItems="center"
                    borderWidth="1px"
                    borderColor={colors.border}
                    borderRightWidth="0"
                    borderRadius="md"
                    borderRightRadius="0"
                    bg={colors.cardBg}
                  >
                    <Text fontSize="sm" color={colors.textSecondary}>
                      From
                    </Text>
                  </Box>
                  <Button
                    {...dateButtonProps}
                    borderLeftRadius="0"
                    onClick={() => {
                      syncDraftCustomFrom()
                      setCustomFromOpen(true)
                    }}
                  >
                    {customFromLabel}
                  </Button>
                </HStack>
              </PopoverTrigger>
              <PopoverContent w="auto" borderColor={colors.border}>
                <PopoverArrow />
                <PopoverHeader fontWeight="bold" border="0" pb={0}>
                  Select from date
                </PopoverHeader>
                <PopoverBody>
                  <DayCalendar
                    viewMonth={draftCustomFromView}
                    onViewMonthChange={setDraftCustomFromView}
                    selectedDay={draftCustomFrom}
                    onSelectDay={(d) => setDraftCustomFrom(d)}
                  />
                </PopoverBody>
                <PopoverFooter border="0" display="flex" justifyContent="flex-end" gap={2}>
                  <Button size="sm" variant="ghost" onClick={() => setCustomFromOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => {
                      setCustomCommitted((c) => ({ ...c, from: startOfDay(draftCustomFrom) }))
                      setCustomFromOpen(false)
                    }}
                  >
                    OK
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
          </VStack>

          <VStack align="flex-start" spacing={1}>
            {/* <Text fontSize="xs" fontWeight="semibold" color={colors.textSecondary}>
              To:
            </Text> */}
            <Popover
              isOpen={customToOpen}
              onClose={() => setCustomToOpen(false)}
              closeOnBlur={false}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <HStack spacing={0}>
                  <Box
                    px={3}
                    h="32px"
                    display="inline-flex"
                    alignItems="center"
                    borderWidth="1px"
                    borderColor={colors.border}
                    borderRightWidth="0"
                    borderRadius="md"
                    borderRightRadius="0"
                    bg={colors.cardBg}
                  >
                    <Text fontSize="sm" color={colors.textSecondary}>
                      To
                    </Text>
                  </Box>
                  <Button
                    {...dateButtonProps}
                    borderLeftRadius="0"
                    onClick={() => {
                      syncDraftCustomTo()
                      setCustomToOpen(true)
                    }}
                  >
                    {customToLabel}
                  </Button>
                </HStack>
              </PopoverTrigger>
              <PopoverContent w="auto" borderColor={colors.border}>
                <PopoverArrow />
                <PopoverHeader fontWeight="bold" border="0" pb={0}>
                  Select to date
                </PopoverHeader>
                <PopoverBody>
                  <DayCalendar
                    viewMonth={draftCustomToView}
                    onViewMonthChange={setDraftCustomToView}
                    selectedDay={draftCustomTo}
                    onSelectDay={(d) => setDraftCustomTo(d)}
                  />
                </PopoverBody>
                <PopoverFooter border="0" display="flex" justifyContent="flex-end" gap={2}>
                  <Button size="sm" variant="ghost" onClick={() => setCustomToOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => {
                      setCustomCommitted((c) => ({ ...c, to: startOfDay(draftCustomTo) }))
                      setCustomToOpen(false)
                    }}
                  >
                    OK
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
          </VStack>
        </HStack>
      ) : (
        <VStack align="flex-start" spacing={1}>
          {/* <Text fontSize="xs" fontWeight="semibold" color={colors.textSecondary}>
            Date:
          </Text> */}

          {filterKind === 'day' && (
            <Popover
              isOpen={dayPopoverOpen}
              onClose={() => setDayPopoverOpen(false)}
              closeOnBlur={false}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <Button
                  {...dateButtonProps}
                  onClick={() => {
                    syncDraftDay()
                    setDayPopoverOpen(true)
                  }}
                >
                  {dayButtonLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent w="auto" borderColor={colors.border}>
                <PopoverArrow />
                <PopoverHeader fontWeight="bold" border="0" pb={0}>
                  Select date
                </PopoverHeader>
                <PopoverBody>
                  <DayCalendar
                    viewMonth={draftDayViewMonth}
                    onViewMonthChange={setDraftDayViewMonth}
                    selectedDay={draftDay}
                    onSelectDay={(d) => setDraftDay(d)}
                  />
                </PopoverBody>
                <PopoverFooter border="0" display="flex" justifyContent="flex-end" gap={2}>
                  <Button size="sm" variant="ghost" onClick={() => setDayPopoverOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => {
                      setDayCommitted(startOfDay(draftDay))
                      setDayPopoverOpen(false)
                    }}
                  >
                    OK
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
          )}

          {filterKind === 'week' && (
            <Popover
              isOpen={weekPopoverOpen}
              onClose={() => setWeekPopoverOpen(false)}
              closeOnBlur={false}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <Button
                  {...dateButtonProps}
                  onClick={() => {
                    syncDraftWeek()
                    setWeekPopoverOpen(true)
                  }}
                >
                  {weekButtonLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent w="auto" borderColor={colors.border}>
                <PopoverArrow />
                <PopoverHeader fontWeight="bold" border="0" pb={0}>
                  Select week
                </PopoverHeader>
                <PopoverBody>
                  <HStack justify="center" mb={2}>
                    <IconButton
                      aria-label="Previous week"
                      icon={<ChevronLeft size={18} />}
                      size="sm"
                      variant="outline"
                      borderColor={colors.border}
                      onClick={() => setDraftWeekMonday((d) => addWeeks(d, -1))}
                    />
                    <Text fontSize="sm" fontWeight="medium" px={2} color={colors.text} minW="180px" textAlign="center">
                      {format(startOfDay(draftWeekMonday), 'MMM d')} –{' '}
                      {format(startOfDay(sundayOfWeek(draftWeekMonday)), 'MMM d, yyyy')}
                    </Text>
                    <IconButton
                      aria-label="Next week"
                      icon={<ChevronRight size={18} />}
                      size="sm"
                      variant="outline"
                      borderColor={colors.border}
                      onClick={() => setDraftWeekMonday((d) => addWeeks(d, 1))}
                    />
                  </HStack>
                  <DayCalendar
                    viewMonth={startOfMonth(draftWeekMonday)}
                    onViewMonthChange={() => {}}
                    selectedDay={draftWeekMonday}
                    disableDayClick
                    highlightWeekMonday={draftWeekMonday}
                    hideMonthNav
                  />
                </PopoverBody>
                <PopoverFooter border="0" display="flex" justifyContent="flex-end" gap={2}>
                  <Button size="sm" variant="ghost" onClick={() => setWeekPopoverOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => {
                      setWeekCommittedMonday(startOfDay(draftWeekMonday))
                      setWeekPopoverOpen(false)
                    }}
                  >
                    OK
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
          )}

          {filterKind === 'month' && (
            <Popover
              isOpen={monthPopoverOpen}
              onClose={() => setMonthPopoverOpen(false)}
              closeOnBlur={false}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <Button
                  {...dateButtonProps}
                  onClick={() => {
                    syncDraftMonth()
                    setMonthPopoverOpen(true)
                  }}
                >
                  {monthButtonLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent w="auto" borderColor={colors.border}>
                <PopoverArrow />
                <PopoverHeader fontWeight="bold" border="0" pb={0}>
                  Select month
                </PopoverHeader>
                <PopoverBody>
                  <HStack justify="space-between" mb={3}>
                    <IconButton
                      aria-label="Previous year"
                      icon={<ChevronLeft size={18} />}
                      size="sm"
                      variant="ghost"
                      onClick={() => setDraftMonthY((y) => y - 1)}
                    />
                    <Text fontWeight="semibold">{draftMonthY}</Text>
                    <IconButton
                      aria-label="Next year"
                      icon={<ChevronRight size={18} />}
                      size="sm"
                      variant="ghost"
                      onClick={() => setDraftMonthY((y) => y + 1)}
                    />
                  </HStack>
                  <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                    {MONTH_NAMES.map((name, idx) => {
                      const selected = draftMonthIndex === idx
                      return (
                        <Button
                          key={name}
                          size="sm"
                          variant={selected ? 'solid' : 'outline'}
                          colorScheme={selected ? 'blue' : 'gray'}
                          onClick={() => setDraftMonthIndex(idx)}
                        >
                          {name}
                        </Button>
                      )
                    })}
                  </Grid>
                </PopoverBody>
                <PopoverFooter border="0" display="flex" justifyContent="flex-end" gap={2}>
                  <Button size="sm" variant="ghost" onClick={() => setMonthPopoverOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => {
                      setMonthCommitted({ y: draftMonthY, m: draftMonthIndex })
                      setMonthPopoverOpen(false)
                    }}
                  >
                    OK
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
          )}

          {filterKind === 'year' && (
            <Popover
              isOpen={yearPopoverOpen}
              onClose={() => setYearPopoverOpen(false)}
              closeOnBlur={false}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <Button
                  {...dateButtonProps}
                  onClick={() => {
                    syncDraftYear()
                    setYearPopoverOpen(true)
                  }}
                >
                  {yearButtonLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent w="260px" borderColor={colors.border}>
                <PopoverArrow />
                <PopoverHeader fontWeight="bold" border="0" pb={0}>
                  Select year
                </PopoverHeader>
                <PopoverBody>
                  <HStack justify="center" spacing={6} py={2}>
                    <IconButton
                      aria-label="Previous year"
                      icon={<ChevronLeft size={22} />}
                      size="md"
                      variant="outline"
                      borderColor={colors.border}
                      onClick={() => setDraftYear((y) => y - 1)}
                    />
                    <Text fontSize="2xl" fontWeight="bold" color={colors.heading} minW="88px" textAlign="center">
                      {draftYear}
                    </Text>
                    <IconButton
                      aria-label="Next year"
                      icon={<ChevronRight size={22} />}
                      size="md"
                      variant="outline"
                      borderColor={colors.border}
                      onClick={() => setDraftYear((y) => y + 1)}
                    />
                  </HStack>
                </PopoverBody>
                <PopoverFooter border="0" display="flex" justifyContent="flex-end" gap={2}>
                  <Button size="sm" variant="ghost" onClick={() => setYearPopoverOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => {
                      setYearCommitted(draftYear)
                      setYearPopoverOpen(false)
                    }}
                  >
                    OK
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
          )}
        </VStack>
      )}
    </HStack>
  )
}
